import React from 'react';
import { FaApple } from 'react-icons/fa';
import { SiAndroid } from 'react-icons/si';
import { FiDownload, FiSmartphone } from 'react-icons/fi';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useLanguage } from '@/hooks/useLanguage';

interface AppStoreDownloadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  playStoreUrl?: string;
  appStoreUrl?: string;
}

export const AppStoreDownloadDialog: React.FC<AppStoreDownloadDialogProps> = ({
  open,
  onOpenChange,
  title = 'Download our app and join thousands of travelers',
  playStoreUrl = 'https://play.google.com/store/apps/details?id=com.hst.duriapp',
  appStoreUrl = 'https://apps.apple.com/in/app/duri/id6755706995'
}) => {
  const { t } = useLanguage();
  const tAppStore = (key: string) => t(key, 'appStore');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-lg sm:max-w-2xl p-0 overflow-visible bg-gradient-to-br from-white via-gray-50 to-blue-50/30 border-0 shadow-2xl [&>button]:z-[60] [&>button]:!right-4 [&>button]:!top-4 [&>button]:!bg-white/90 [&>button]:!hover:bg-white [&>button]:!rounded-full [&>button]:!p-2 [&>button]:!shadow-lg"
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-lg">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#22C55E]/10 to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-blue-400/10 to-transparent rounded-full blur-2xl animate-float-slow"></div>
        </div>

        {/* Content with glassmorphism */}
        <div className="relative z-10 p-6 sm:p-8 md:p-10">
          <DialogHeader className="mb-8">
            {/* Icon badge */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#22C55E] to-orange-600 rounded-2xl flex items-center justify-center shadow-xl animate-fade-in-up">
                <FiSmartphone className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
            </div>
            
            <DialogTitle className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-900 text-center mb-3 leading-tight animate-fade-in-up-delayed">
              <span className="block">{title}</span>
            </DialogTitle>
            
            <p className="text-sm sm:text-base text-gray-600 text-center font-light animate-fade-in-up-delayed-2">
              Experience the best ride-sharing platform on your mobile device
            </p>
          </DialogHeader>

          {/* App Store Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 sm:gap-6 mt-8 animate-fade-in-up-delayed-3">
            {/* Google Play Store Button */}
            <a
              id='google-play-button'
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-white/95 hover:bg-white backdrop-blur-xl rounded-2xl px-5 py-4 sm:px-6 sm:py-4 md:px-7 md:py-5 shadow-2xl hover:shadow-[#3DDC84]/30 transition-all duration-500 border-2 border-white/40 hover:border-[#3DDC84]/30 transform hover:scale-105 hover:-translate-y-1 w-full sm:w-auto min-w-[200px] sm:min-w-[220px]"
            >
              {/* Animated gradient background */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#3DDC84]/5 to-[#2AB665]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Shine effect on hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shimmer"></div>
              
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#3DDC84] to-[#2AB665] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500">
                  <SiAndroid className="text-white text-xl sm:text-2xl md:text-3xl" />
                </div>
                <div className="text-left flex-1">
                  <div className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 font-semibold leading-tight uppercase tracking-wider mb-0.5">
                    {tAppStore('getItOn') || 'GET IT ON'}
                  </div>
                  <div className="text-base sm:text-lg md:text-xl font-bold text-gray-900 leading-tight">
                    {tAppStore('googlePlay') || 'Google Play'}
                  </div>
                </div>
                <FiDownload className="w-5 h-5 text-gray-400 group-hover:text-[#3DDC84] group-hover:translate-y-1 transition-all duration-300 opacity-0 group-hover:opacity-100" />
              </div>
            </a>

            {/* Apple App Store Button */}
            <a
              id='app-store-button'
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-white/95 hover:bg-white backdrop-blur-xl rounded-2xl px-5 py-4 sm:px-6 sm:py-4 md:px-7 md:py-5 shadow-2xl hover:shadow-gray-900/30 transition-all duration-500 border-2 border-white/40 hover:border-gray-800/30 transform hover:scale-105 hover:-translate-y-1 w-full sm:w-auto min-w-[200px] sm:min-w-[220px]"
            >
              {/* Animated gradient background */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-800/5 to-gray-900/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Shine effect on hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shimmer"></div>
              
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500">
                  <FaApple className="text-white text-xl sm:text-2xl md:text-3xl" />
                </div>
                <div className="text-left flex-1">
                  <div className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 font-semibold leading-tight uppercase tracking-wider mb-0.5">
                    {tAppStore('downloadOnThe') || 'DOWNLOAD ON THE'}
                  </div>
                  <div className="text-base sm:text-lg md:text-xl font-bold text-gray-900 leading-tight">
                    {tAppStore('appStore') || 'App Store'}
                  </div>
                </div>
                <FiDownload className="w-5 h-5 text-gray-400 group-hover:text-gray-800 group-hover:translate-y-1 transition-all duration-300 opacity-0 group-hover:opacity-100" />
              </div>
            </a>
          </div>

          {/* Additional info */}
          <div className="mt-8 text-center animate-fade-in-up-delayed-3">
            <p className="text-xs sm:text-sm text-gray-500 font-light">
              Available on iOS and Android devices
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

